import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const RULE_TYPES = [
  { value: 'field_map', label: 'Field Map', color: '#0ABFA3', desc: 'Map source field to target field' },
  { value: 'type_cast', label: 'Type Cast', color: '#9F7AEA', desc: 'Convert field type with optional operation' },
  { value: 'conditional', label: 'Conditional', color: '#C8A030', desc: 'If/then logic to filter or skip' },
  { value: 'static_value', label: 'Static Value', color: '#34D399', desc: 'Set a fixed value on target field' },
];

const OPERATIONS = ['to_float', 'to_int', 'to_string'];
const CONDITION_OPS = ['eq', 'neq', 'gt', 'lt', 'contains'];

const colorMap = { field_map: '#0ABFA3', type_cast: '#9F7AEA', conditional: '#C8A030', static_value: '#34D399' };

/* ─── RULE EDITOR ROW ─── */
function RuleRow({ rule, index, onChange, onRemove, onMove, totalRules }) {
  const color = colorMap[rule.rule_type] || '#0ABFA3';

  const update = (field, value) => {
    onChange(index, { ...rule, [field]: value });
  };

  return (
    <div
      className="bg-kova-card2 border border-kova-cyan/12 p-3 space-y-2"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}
      data-testid={`rule-row-${index}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="text-[9px] font-mono px-2 py-0.5 uppercase tracking-wider border"
          style={{ background: `${color}15`, color, borderColor: `${color}40` }}
        >
          {rule.rule_type?.replace('_', ' ')}
        </span>
        <span className="text-[9px] text-kova-slate font-mono">Rule {index + 1}</span>
        <div className="ml-auto flex items-center gap-1">
          {index > 0 && (
            <button onClick={() => onMove(index, -1)} className="text-[10px] text-kova-slate hover:text-white px-1" title="Move up">&uarr;</button>
          )}
          {index < totalRules - 1 && (
            <button onClick={() => onMove(index, 1)} className="text-[10px] text-kova-slate hover:text-white px-1" title="Move down">&darr;</button>
          )}
          <button onClick={() => onRemove(index)} className="text-[10px] text-kova-slate hover:text-kova-red px-1" title="Delete">&times;</button>
        </div>
      </div>

      {/* FIELD MAP */}
      {rule.rule_type === 'field_map' && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder="$.source.field"
            value={rule.source_field || ''}
            onChange={e => update('source_field', e.target.value)}
            className="bg-kova-cyan/8 border border-kova-cyan/20 text-kova-cyan font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[140px]"
            data-testid={`rule-${index}-source`}
          />
          <span className="text-kova-slate text-xs">&rarr;</span>
          <input
            placeholder="$.target.field"
            value={rule.target_field || ''}
            onChange={e => update('target_field', e.target.value)}
            className="bg-white/4 border border-kova-cyan/12 text-white font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[140px]"
            data-testid={`rule-${index}-target`}
          />
        </div>
      )}

      {/* TYPE CAST */}
      {rule.rule_type === 'type_cast' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              placeholder="$.source.field"
              value={rule.source_field || ''}
              onChange={e => update('source_field', e.target.value)}
              className="bg-kova-cyan/8 border border-kova-cyan/20 text-kova-cyan font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[120px]"
            />
            <span className="text-kova-slate text-xs">&rarr;</span>
            <select
              value={rule.operation || 'to_string'}
              onChange={e => update('operation', e.target.value)}
              className="bg-kova-purple/10 border border-kova-purple/25 text-kova-purple font-mono text-[11px] px-2 py-1 outline-none"
            >
              {OPERATIONS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
            <input
              placeholder="e.g. / 100"
              value={rule.operation_value || ''}
              onChange={e => update('operation_value', e.target.value)}
              className="bg-kova-purple/8 border border-kova-purple/20 text-kova-purple font-mono text-[11px] px-2 py-1 outline-none w-20"
            />
            <span className="text-kova-slate text-xs">&rarr;</span>
            <input
              placeholder="$.target.field"
              value={rule.target_field || ''}
              onChange={e => update('target_field', e.target.value)}
              className="bg-white/4 border border-kova-cyan/12 text-white font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[120px]"
            />
          </div>
        </div>
      )}

      {/* CONDITIONAL */}
      {rule.rule_type === 'conditional' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
            <span className="text-kova-gold">if</span>
            <input
              placeholder="$.field"
              value={rule.condition_field || ''}
              onChange={e => update('condition_field', e.target.value)}
              className="bg-kova-cyan/8 border border-kova-cyan/20 text-kova-cyan px-2 py-1 outline-none w-32"
            />
            <select
              value={rule.condition_op || 'eq'}
              onChange={e => update('condition_op', e.target.value)}
              className="bg-kova-gold/10 border border-kova-gold/25 text-kova-gold px-2 py-1 outline-none"
            >
              {CONDITION_OPS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
            <input
              placeholder="value"
              value={rule.condition_value || ''}
              onChange={e => update('condition_value', e.target.value)}
              className="bg-white/4 border border-kova-cyan/12 text-white px-2 py-1 outline-none w-24"
            />
            <span className="text-kova-gold">then</span>
            <select
              value={rule.condition_action || 'continue'}
              onChange={e => update('condition_action', e.target.value)}
              className="bg-kova-gold/10 border border-kova-gold/25 text-kova-gold px-2 py-1 outline-none"
            >
              <option value="continue">continue</option>
              <option value="skip">skip</option>
            </select>
          </div>
          <p className="text-[9px] text-kova-slate">{rule.label || 'Records not matching this condition will be filtered out'}</p>
        </div>
      )}

      {/* STATIC VALUE */}
      {rule.rule_type === 'static_value' && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder='"static_value"'
            value={rule.static_value || ''}
            onChange={e => update('static_value', e.target.value)}
            className="bg-kova-green/8 border border-kova-green/20 text-kova-green font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[120px]"
            data-testid={`rule-${index}-static`}
          />
          <span className="text-kova-slate text-xs">&rarr;</span>
          <input
            placeholder="$.target.field"
            value={rule.target_field || ''}
            onChange={e => update('target_field', e.target.value)}
            className="bg-white/4 border border-kova-cyan/12 text-white font-mono text-[11px] px-2 py-1 outline-none flex-1 min-w-[140px]"
          />
        </div>
      )}
    </div>
  );
}

/* ─── ADD RULE PANEL ─── */
function AddRulePanel({ onAdd }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="add-rule-panel">
      {RULE_TYPES.map(rt => (
        <button
          key={rt.value}
          onClick={() => onAdd(rt.value)}
          className="p-3 bg-kova-card border border-kova-cyan/12 hover:border-opacity-60 text-left transition-colors"
          style={{ borderLeftWidth: 3, borderLeftColor: rt.color }}
          data-testid={`add-rule-${rt.value}`}
        >
          <div className="text-xs font-mono font-semibold" style={{ color: rt.color }}>{rt.label}</div>
          <div className="text-[10px] text-kova-slate mt-1">{rt.desc}</div>
        </button>
      ))}
    </div>
  );
}

/* ─── PREVIEW PANEL ─── */
function PreviewPanel({ rulesetId, rules }) {
  const [input, setInput] = useState('{\n  "amount": 2500,\n  "currency": "usd",\n  "customer": {\n    "email": "test@example.com"\n  }\n}');
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    if (!rulesetId) { toast.error('Save the rule set first'); return; }
    setLoading(true);
    try {
      let parsedInput;
      try { parsedInput = JSON.parse(input); }
      catch { toast.error('Invalid JSON input'); setLoading(false); return; }

      const res = await fetch(`${API}/transforms/${rulesetId}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ input: parsedInput })
      });
      if (res.ok) {
        const data = await res.json();
        setOutput(data);
      } else {
        toast.error('Preview failed');
      }
    } catch (e) { toast.error('Preview error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="card-brutal p-4 space-y-3" data-testid="preview-panel">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Preview · Test with sample data</span>
        <Button
          onClick={runPreview}
          disabled={loading || !rulesetId}
          size="sm"
          className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[10px] tracking-wider uppercase"
          data-testid="run-preview-btn"
        >
          {loading ? 'Running...' : 'Preview'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[9px] font-mono text-kova-slate uppercase tracking-wider mb-1">Input</div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            className="w-full h-40 bg-kova-card border border-kova-cyan/12 text-kova-cyan font-mono text-[11px] p-3 outline-none resize-none focus:border-kova-cyan/30"
            data-testid="preview-input"
          />
        </div>
        <div>
          <div className="text-[9px] font-mono text-kova-slate uppercase tracking-wider mb-1">
            Output {output?.skipped && <span className="text-kova-gold ml-2">SKIPPED (conditional filter)</span>}
          </div>
          <pre className="w-full h-40 bg-kova-card border border-kova-cyan/12 text-kova-green font-mono text-[11px] p-3 overflow-auto">
            {output ? JSON.stringify(output.output, null, 2) : '// Run preview to see output'}
          </pre>
        </div>
      </div>
      {output && (
        <div className="flex items-center gap-4 text-[10px] font-mono">
          <span className="text-kova-slate">Rules applied: <span className="text-white">{output.rules_applied}</span></span>
          <span className={output.skipped ? 'text-kova-gold' : 'text-kova-green'}>{output.skipped ? 'Record filtered out by conditional' : 'All rules passed'}</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   TRANSFORMS PAGE
   ═══════════════════════════════════════ */
export default function Transforms() {
  const [rulesets, setRulesets] = useState([]);
  const [activeRuleset, setActiveRuleset] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editName, setEditName] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [targetLabel, setTargetLabel] = useState('');

  const fetchRulesets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/transforms`, { credentials: 'include' });
      if (res.ok) setRulesets(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRulesets(); }, [fetchRulesets]);

  const selectRuleset = (rs) => {
    setActiveRuleset(rs);
    setRules(rs.rules || []);
    setEditName(rs.name);
    setSourceLabel(rs.source_label || '');
    setTargetLabel(rs.target_label || '');
    setShowAddRule(false);
  };

  const createNew = async () => {
    try {
      const res = await fetch(`${API}/transforms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: 'New Transform Rule Set', rules: [] })
      });
      if (res.ok) {
        const rs = await res.json();
        setRulesets(prev => [rs, ...prev]);
        selectRuleset(rs);
        toast.success('Rule set created');
      }
    } catch (e) { toast.error('Failed to create rule set'); }
  };

  const saveRuleset = async () => {
    if (!activeRuleset) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/transforms/${activeRuleset.ruleset_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName, rules, source_label: sourceLabel, target_label: targetLabel })
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveRuleset(updated);
        setRulesets(prev => prev.map(r => r.ruleset_id === updated.ruleset_id ? updated : r));
        toast.success('Saved');
      }
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const deleteRuleset = async (id) => {
    try {
      const res = await fetch(`${API}/transforms/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setRulesets(prev => prev.filter(r => r.ruleset_id !== id));
        if (activeRuleset?.ruleset_id === id) { setActiveRuleset(null); setRules([]); }
        toast.success('Deleted');
      }
    } catch (e) { toast.error('Delete failed'); }
  };

  const addRule = (ruleType) => {
    const newRule = { rule_type: ruleType };
    if (ruleType === 'field_map') { newRule.source_field = ''; newRule.target_field = ''; }
    else if (ruleType === 'type_cast') { newRule.source_field = ''; newRule.target_field = ''; newRule.operation = 'to_float'; newRule.operation_value = ''; }
    else if (ruleType === 'conditional') { newRule.condition_field = ''; newRule.condition_op = 'eq'; newRule.condition_value = ''; newRule.condition_action = 'continue'; }
    else if (ruleType === 'static_value') { newRule.static_value = ''; newRule.target_field = ''; }
    setRules(prev => [...prev, newRule]);
    setShowAddRule(false);
  };

  const updateRule = (index, updated) => { setRules(prev => prev.map((r, i) => i === index ? updated : r)); };
  const removeRule = (index) => { setRules(prev => prev.filter((_, i) => i !== index)); };
  const moveRule = (index, dir) => {
    const newRules = [...rules];
    const target = index + dir;
    [newRules[index], newRules[target]] = [newRules[target], newRules[index]];
    setRules(newRules);
  };

  return (
    <div className="space-y-4" data-testid="transforms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-lg">Data Transforms</h1>
          <p className="text-[10px] text-kova-slate font-mono mt-0.5">Visual rule builder for field mapping, type coercion, and conditional routing</p>
        </div>
        <Button
          onClick={createNew}
          className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-xs tracking-wider uppercase"
          data-testid="new-ruleset-btn"
        >
          + New Rule Set
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Sidebar: Rule set list */}
        <div className="card-brutal divide-y divide-kova-cyan/12 overflow-hidden" data-testid="ruleset-list">
          {loading ? (
            <div className="p-6 text-center text-kova-slate font-mono text-sm">Loading...</div>
          ) : rulesets.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-kova-slate font-mono text-sm">No rule sets</p>
              <p className="text-kova-slate text-[10px] mt-1">Create one to start mapping fields</p>
            </div>
          ) : (
            rulesets.map(rs => (
              <div
                key={rs.ruleset_id}
                className={`px-4 py-3 cursor-pointer transition-colors ${activeRuleset?.ruleset_id === rs.ruleset_id ? 'bg-kova-cyan/8 border-l-2 border-kova-cyan' : 'hover:bg-kova-card/50'}`}
                onClick={() => selectRuleset(rs)}
                data-testid={`ruleset-item-${rs.ruleset_id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{rs.name}</span>
                  <button
                    onClick={e => { e.stopPropagation(); deleteRuleset(rs.ruleset_id); }}
                    className="text-[10px] text-kova-slate hover:text-kova-red"
                    data-testid={`delete-ruleset-${rs.ruleset_id}`}
                  >
                    &times;
                  </button>
                </div>
                <div className="text-[10px] text-kova-slate font-mono mt-0.5">{rs.rules?.length || 0} rules · {rs.is_active ? 'active' : 'inactive'}</div>
              </div>
            ))
          )}
        </div>

        {/* Main: Rule editor */}
        <div className="space-y-4">
          {!activeRuleset ? (
            <div className="card-brutal p-12 text-center">
              <p className="text-kova-slate font-mono">Select or create a rule set to begin</p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="card-brutal p-3 flex items-center gap-3 flex-wrap">
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="bg-kova-card border border-kova-cyan/12 text-white font-mono text-sm px-3 py-1.5 outline-none focus:border-kova-cyan/40 flex-1 min-w-[180px]"
                  data-testid="ruleset-name-input"
                />
                <div className="flex items-center gap-2 text-[10px] font-mono">
                  <input
                    value={sourceLabel}
                    onChange={e => setSourceLabel(e.target.value)}
                    placeholder="Source label"
                    className="bg-kova-card border border-kova-cyan/12 text-kova-cyan px-2 py-1 outline-none w-28"
                    data-testid="source-label-input"
                  />
                  <span className="text-kova-slate">&rarr;</span>
                  <input
                    value={targetLabel}
                    onChange={e => setTargetLabel(e.target.value)}
                    placeholder="Target label"
                    className="bg-kova-card border border-kova-cyan/12 text-white px-2 py-1 outline-none w-28"
                    data-testid="target-label-input"
                  />
                </div>
                <span className="text-[9px] font-mono px-2 py-0.5 bg-kova-green/10 text-kova-green border border-kova-green/20 tracking-wider uppercase">
                  {rules.length} RULE{rules.length !== 1 ? 'S' : ''}
                </span>
                <Button
                  onClick={saveRuleset}
                  disabled={saving}
                  size="sm"
                  className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[10px] tracking-wider uppercase ml-auto"
                  data-testid="save-ruleset-btn"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>

              {/* Rules */}
              <div className="space-y-2">
                <AnimatePresence>
                  {rules.map((rule, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                    >
                      <RuleRow
                        rule={rule}
                        index={i}
                        onChange={updateRule}
                        onRemove={removeRule}
                        onMove={moveRule}
                        totalRules={rules.length}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Add rule trigger */}
                {showAddRule ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <AddRulePanel onAdd={addRule} />
                  </motion.div>
                ) : (
                  <button
                    onClick={() => setShowAddRule(true)}
                    className="w-full p-3 border border-dashed border-kova-cyan/12 text-kova-slate font-mono text-xs hover:text-kova-cyan hover:border-kova-cyan/30 transition-colors"
                    data-testid="show-add-rule-btn"
                  >
                    + Add transformation rule
                  </button>
                )}
              </div>

              {/* Preview */}
              <PreviewPanel rulesetId={activeRuleset?.ruleset_id} rules={rules} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
